import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  EcaSummaryData,
  LoadFileData,
} from '../../components/client-details/project-data-table/types';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../components/eca-doc-list/eca-save-search/EcaSaveSearch';
import {SavedSearchDetails} from '../../components/eca/SavedSearchPanel';
import {RelationalGroup} from '../../components/pages/index-search/RelationshipGroupPopup';

export type IGlobalSelectedItem =
  | LoadFileData
  | EcaSummaryData
  | null
  | TreeViewBaseItem<ExtendedTreeItemProps>;

export interface IECAAdhocSearchState {
  isSearchFromECAHome: boolean;
  isDeduplicationModal: boolean;
  closeFlyout: boolean;
  globalSelectedItem: IGlobalSelectedItem;
  searchResultError: string | null;
  allDocumentsData: LoadFileData | null;
  searchTreeItemList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  globalSavedSearchDetails: SavedSearchDetails | null;
  showSavedSearchConfirmDeletePopup: boolean;
  globalSelectedScopeItem: IGlobalSelectedItem;
  disableSavedSearchButton: boolean;
  ecaDrawerFlag: boolean;
  checkEcaAdmin: boolean;
  addGroupCheck: boolean;
  manageSavedSearch: boolean;
  relationalGroupsList?: RelationalGroup[];
  showSetScopeLoader: boolean;
  loadFiles: LoadFileData[];
  selectedLoadFile: LoadFileData | null;
  savedSearchLastRun: boolean;
  searchLoader: boolean;
  isQuerySaved: boolean;
  paginationDefaultLoad: boolean;
  showExportManagement: boolean;
}

const initialState: IECAAdhocSearchState = {
  isSearchFromECAHome: false,
  isDeduplicationModal: false,
  closeFlyout: false,
  globalSelectedItem: null,
  searchResultError: null,
  allDocumentsData: null,
  searchTreeItemList: [],
  globalSavedSearchDetails: null,
  showSavedSearchConfirmDeletePopup: false,
  globalSelectedScopeItem: null,
  disableSavedSearchButton: false,
  ecaDrawerFlag: false,
  checkEcaAdmin: false,
  addGroupCheck: false,
  manageSavedSearch: true,
  relationalGroupsList: [],
  showSetScopeLoader: false,
  loadFiles: [],
  selectedLoadFile: null,
  savedSearchLastRun: true,
  searchLoader: false,
  isQuerySaved: false,
  paginationDefaultLoad: false,
  showExportManagement: false,
};

const ecaAdhocSearchSlice = createSlice({
  name: 'ecaAdhocSearchSlice',
  initialState,
  reducers: {
    // Existing reducers...

    setIsSearchFromECAHome(state, action: PayloadAction<boolean>) {
      state.isSearchFromECAHome = action.payload;
    },
    setIsDeduplicationModal(state, action: PayloadAction<boolean>) {
      state.isDeduplicationModal = action.payload;
    },
    setCloseFlyout(state, action: PayloadAction<boolean>) {
      state.closeFlyout = action.payload;
    },
    setGlobalSelectedItem(state, action: PayloadAction<IGlobalSelectedItem>) {
      state.globalSelectedItem = action.payload;
    },
    setAllDocumentsData(state, action: PayloadAction<LoadFileData>) {
      state.allDocumentsData = action.payload;
    },
    setSearchTreeItemList(
      state,
      action: PayloadAction<TreeViewBaseItem<ExtendedTreeItemProps>[]>
    ) {
      state.searchTreeItemList = action.payload;
    },
    setGlobalSavedSearchDetails(
      state,
      action: PayloadAction<SavedSearchDetails | null>
    ) {
      state.globalSavedSearchDetails = action.payload;
    },
    setSavedSearchDeleteConfirm(state, action: PayloadAction<boolean>) {
      state.showSavedSearchConfirmDeletePopup = action.payload;
    },
    setSearchResultError(state, action: PayloadAction<string | null>) {
      state.searchResultError = action.payload;
    },
    setGlobalSelectedScopeItem(
      state,
      action: PayloadAction<IGlobalSelectedItem>
    ) {
      state.globalSelectedScopeItem = action.payload;
    },
    setDisableSavedSearchButton(state, action: PayloadAction<boolean>) {
      state.disableSavedSearchButton = action.payload;
    },
    setEcaDrawerFlag(state, action: PayloadAction<boolean>) {
      state.ecaDrawerFlag = action.payload;
    },
    setEcaAdminPermission(state, action: PayloadAction<boolean>) {
      state.checkEcaAdmin = action.payload;
    },
    setAddGroupCheck(state, action: PayloadAction<boolean>) {
      state.addGroupCheck = action.payload;
    },
    setManageSavedSearch(state, action: PayloadAction<boolean>) {
      state.manageSavedSearch = action.payload;
    },
    setRelationalGroupsList(state, action: PayloadAction<RelationalGroup[]>) {
      state.relationalGroupsList = action.payload;
    },
    setShowSetScopeLoader(state, action: PayloadAction<boolean>) {
      state.showSetScopeLoader = action.payload;
    },
    setLoadFiles(state, action: PayloadAction<LoadFileData[]>) {
      state.loadFiles = action.payload;
    },
    setSelectedLoadFile(state, action: PayloadAction<LoadFileData | null>) {
      state.selectedLoadFile = action.payload;
    },
    setSavedSearchLastRun(state, action: PayloadAction<boolean>) {
      state.savedSearchLastRun = action.payload;
    },
    setSearchLoader(state, action: PayloadAction<boolean>) {
      state.searchLoader = action.payload;
    },

    setIsQuerySaved(state, action: PayloadAction<boolean>) {
      state.isQuerySaved = action.payload;
    },
    setPaginationDefaultLoad(state, action: PayloadAction<boolean>) {
      state.paginationDefaultLoad = action.payload;
    },
    setShowExportManagement(state, action: PayloadAction<boolean>) {
      state.showExportManagement = action.payload;
    },
  },
});

export const {
  setIsSearchFromECAHome,
  setIsDeduplicationModal,
  setCloseFlyout,
  setGlobalSelectedItem,
  setAllDocumentsData,
  setSearchTreeItemList,
  setGlobalSavedSearchDetails,
  setSavedSearchDeleteConfirm,
  setSearchResultError,
  setGlobalSelectedScopeItem,
  setEcaDrawerFlag,
  setEcaAdminPermission,
  setAddGroupCheck,
  setManageSavedSearch,
  setRelationalGroupsList,
  setShowSetScopeLoader,
  setLoadFiles,
  setSelectedLoadFile,
  setSavedSearchLastRun,
  setSearchLoader,
  setIsQuerySaved,
  setPaginationDefaultLoad,
  setShowExportManagement,
} = ecaAdhocSearchSlice.actions;

export default ecaAdhocSearchSlice.reducer;
